import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');

// Products
export const getProducts = (category) => api.get('/products', { params: { category } });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);

// Cart
export const getCart = () => api.get('/cart');
export const addToCart = (data) => api.post('/cart', data);
export const removeFromCart = (id) => api.delete(`/cart/${id}`);
export const clearCart = () => api.delete('/cart');

// Payment Methods
export const getPaymentMethodsGlobal = () => api.get('/payment-methods');

// Orders (Manual Payment)
export const createOrder = (data) => api.post('/orders', data);
export const getMyOrders = () => api.get('/orders/my-orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const uploadOrderProof = (orderId, formData) => api.post(`/orders/${orderId}/upload-proof`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Admin Orders
export const getAdminOrders = (status) => api.get('/admin/orders', { params: { status } });
export const approveOrder = (orderId) => api.put(`/admin/orders/${orderId}/approve`);
export const rejectOrder = (orderId, reason) => api.put(`/admin/orders/${orderId}/reject`, null, { params: { reason } });
export const cancelOrder = (orderId, reason) => api.put(`/admin/orders/${orderId}/cancel`, null, { params: { reason } });
export const shipOrder = (orderId, trackingNumber) => api.put(`/admin/orders/${orderId}/ship`, null, { params: { tracking_number: trackingNumber } });
export const completeOrder = (orderId) => api.put(`/admin/orders/${orderId}/complete`);

// Blog
export const getBlogPosts = () => api.get('/blog');
export const getBlogPost = (id) => api.get(`/blog/${id}`);
export const createBlogPost = (data) => api.post('/blog', data);
export const updateBlogPost = (id, data) => api.put(`/blog/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/blog/${id}`);

// Blog Comments
export const getBlogComments = (postId) => api.get(`/blog/${postId}/comments`);
export const createBlogComment = (postId, data) => api.post(`/blog/${postId}/comments`, data);
export const deleteBlogComment = (postId, commentId) => api.delete(`/blog/${postId}/comments/${commentId}`);

// Events (Admin)
export const getEvents = (includeUnpublished = false) => api.get('/events', { params: { include_unpublished: includeUnpublished } });
export const createEvent = (data) => api.post('/admin/events', data);
export const updateEvent = (id, data) => api.put(`/admin/events/${id}`, data);
export const deleteEvent = (id) => api.delete(`/admin/events/${id}`);
export const toggleEventPublish = (id) => api.put(`/admin/events/${id}/toggle-publish`);

// File Upload (Admin)
export const uploadEbook = (formData) => api.post('/admin/upload/ebook', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const uploadImage = (formData) => api.post('/admin/upload/image', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});

// Products (Admin)
export const getAdminProducts = (category) => api.get('/products', { params: { category, include_unpublished: true } });
export const updateProduct = (id, data) => api.put(`/admin/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/admin/products/${id}`);
export const toggleProductPublish = (id) => api.put(`/admin/products/${id}/toggle-publish`);

// Bookings
export const getAvailableSlots = (date) => api.get('/bookings/available-slots', { params: { date } });
export const getPaymentMethods = () => api.get('/bookings/payment-methods');
export const createBooking = (data) => api.post('/bookings/create', data);
export const uploadPaymentProof = (bookingId, data) => api.post(`/bookings/${bookingId}/payment-proof`, data);
export const uploadBookingProofFile = (bookingId, formData) => api.post(`/bookings/${bookingId}/upload-proof`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getMyBookings = () => api.get('/bookings/my-bookings');
export const requestReschedule = (bookingId, data) => api.post(`/bookings/${bookingId}/request-reschedule`, data);
export const uploadRescheduleDocument = (bookingId, formData) => api.post(`/bookings/${bookingId}/reschedule-document`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const uploadUlasDeclaration = (bookingId, formData) => api.post(`/bookings/${bookingId}/ulas-declaration`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getMyNotifications = () => api.get('/bookings/my-notifications');

// Admin Bookings
export const getAllBookings = (status) => api.get('/bookings/admin/all', { params: { status } });
export const updateBookingStatus = (bookingId, data) => api.put(`/bookings/admin/${bookingId}`, data);
export const deleteBooking = (bookingId) => api.delete(`/bookings/admin/${bookingId}`);
export const createCustomSlot = (data) => api.post('/bookings/admin/slots', data);
export const getAdminSlots = (date) => api.get('/bookings/admin/slots', { params: { date } });
export const deleteCustomSlot = (slotId) => api.delete(`/bookings/admin/slots/${slotId}`);
export const getCalendarData = (month, year) => api.get('/bookings/admin/calendar', { params: { month, year } });
export const getCategoryStats = () => api.get('/bookings/admin/category-stats');

// Newsroom
export const getNewsArticles = (limit) => api.get('/newsroom/news', { params: { limit } });

// Notices (Top Banner)
export const getNotices = () => api.get('/notices');
export const getActiveNotices = () => api.get('/notices/active');
export const createNotice = (data) => api.post('/notices', data);
export const updateNotice = (id, data) => api.put(`/notices/${id}`, data);
export const deleteNotice = (id) => api.delete(`/notices/${id}`);
export const toggleNotice = (id) => api.put(`/notices/${id}/toggle`);

// Site Settings (Contact & Social Media)
export const getSiteSettings = () => api.get('/settings');
export const updateSiteSettings = (data) => api.put('/settings', data);

// Analytics & Revenue
export const trackVisit = (data) => api.post('/track/visit', data);
export const getAnalytics = () => api.get('/admin/analytics');
export const getRevenueData = (params) => api.get('/admin/revenue', { params });

// CMS - Applications
export const getApplications = (includeUnpublished = false) => api.get('/cms/applications', { params: { include_unpublished: includeUnpublished } });
export const getApplication = (id) => api.get(`/cms/applications/${id}`);
export const createApplication = (data) => api.post('/cms/applications', data);
export const updateApplication = (id, data) => api.put(`/cms/applications/${id}`, data);
export const deleteApplication = (id) => api.delete(`/cms/applications/${id}`);

// CMS - About Us
export const getAboutUsContent = () => api.get('/cms/about-us');
export const updateAboutUsContent = (data) => api.put('/cms/about-us', data);

// CMS - Legal Pages
export const getLegalPages = () => api.get('/cms/legal-pages');
export const getLegalPage = (pageType) => api.get(`/cms/legal-pages/${pageType}`);
export const updateLegalPage = (pageType, data) => api.put(`/cms/legal-pages/${pageType}`, data);

// CMS - FAQs
export const getFAQs = (includeUnpublished = false) => api.get('/cms/faqs', { params: { include_unpublished: includeUnpublished } });
export const getFAQCategories = () => api.get('/cms/faqs/categories');
export const createFAQ = (data) => api.post('/cms/faqs', data);
export const updateFAQ = (id, data) => api.put(`/cms/faqs/${id}`, data);
export const deleteFAQ = (id) => api.delete(`/cms/faqs/${id}`);

export default api;